import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { History, AlertCircle, CheckCircle2, Wallet, Download } from 'lucide-react';
import { cn } from '../lib/utils';

interface LedgerEntry {
    type: string;
    date?: string;
    label: string;
    debit?: number | null;
    credit?: number | null;
    balance?: number;
    payment_status?: string | null;
    target_bill_heads?: { code?: string; name?: string } | null;
}

interface LedgerData {
    unit?: { full_unit_id?: string; unit_number?: string };
    entries: LedgerEntry[];
    advance_balance: number;
    outstanding_by_head: { bill_head: string; outstanding: number }[];
    multi_unit_summary?: { total_outstanding: number; unit_labels: string[] };
}

const LedgerView: React.FC<{ activeUnitId?: string }> = ({ activeUnitId }) => {
    const [ledger, setLedger] = useState<LedgerData | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!activeUnitId) return;
        setLoading(true);
        api.get('/ledger', { params: { unit_id: activeUnitId } })
            .then(res => setLedger(res.data))
            .catch(() => setLedger(null))
            .finally(() => setLoading(false));
    }, [activeUnitId]);

    const entries = ledger?.entries || [];
    const totalBilled = entries.reduce((acc, e) => acc + (e.debit || 0), 0);
    const totalPaid = entries.reduce((acc, e) => acc + (e.credit || 0), 0);
    const totalLateFees = entries.filter((e) => e.type === "late_fee").reduce((acc, e) => acc + (e.debit || 0), 0);
    const outstanding = ledger?.outstanding_by_head?.reduce((a, r) => a + r.outstanding, 0)
        ?? ledger?.multi_unit_summary?.total_outstanding
        ?? Math.max(0, totalBilled - totalPaid);

    if (loading) {
        return <div className="p-8 text-center text-slate-500">Loading ledger…</div>;
    }

    return (
        <div className="space-y-8 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Financial Ledger</h1>
                    <p className="text-slate-500 mt-1">
                        {ledger?.unit?.full_unit_id || ledger?.unit?.unit_number || 'Unit ledger'} — same data as society billing
                    </p>
                </div>
                <button type="button" className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all">
                    <Download size={16} />
                    Export PDF
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl shadow-slate-200">
                    <div className="flex items-center gap-3 mb-4 text-slate-400">
                        <Wallet size={18} />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Outstanding</span>
                    </div>
                    <p className="text-3xl font-bold">₹{outstanding.toLocaleString()}</p>
                    {ledger?.advance_balance ? (
                        <p className="text-[10px] text-slate-500 mt-2 font-medium">Advance: ₹{ledger.advance_balance.toLocaleString()}</p>
                    ) : null}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-4 text-emerald-600">
                        <CheckCircle2 size={18} />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Paid to Date</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">₹{totalPaid.toLocaleString()}</p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-4 text-amber-500">
                        <AlertCircle size={18} />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total Billed</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">₹{totalBilled.toLocaleString()}</p>
                    {totalLateFees > 0 && (
                        <p className="text-xs text-amber-600 mt-2 font-medium">Incl. ₹{totalLateFees.toLocaleString()} late fees</p>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                    <History size={18} className="text-slate-400" />
                    <h2 className="font-bold text-slate-900">Transaction History</h2>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500">
                        <tr>
                            <th className="px-6 py-4 text-left font-bold uppercase text-[10px]">Date</th>
                            <th className="px-6 py-4 text-left font-bold uppercase text-[10px]">Description</th>
                            <th className="px-6 py-4 text-right font-bold uppercase text-[10px]">Debit</th>
                            <th className="px-6 py-4 text-right font-bold uppercase text-[10px]">Credit</th>
                            <th className="px-6 py-4 text-right font-bold uppercase text-[10px]">Balance</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {entries.map((entry) => {
                            const isLateFee = entry.type === "late_fee";
                            const isAdjustment = entry.type === "adjustment";
                            return (
                                <tr key={`${entry.type}-${entry.date}-${entry.label}`} className="hover:bg-slate-50/50">
                                    <td className="px-6 py-4 text-slate-500">{entry.date}</td>
                                    <td className="px-6 py-4 font-medium text-slate-900">
                                        <div className={`flex items-center gap-2 ${(isLateFee || isAdjustment) ? "pl-4" : ""}`}>
                                            {(isLateFee || isAdjustment) && <span className="text-slate-400 font-mono text-xs">↳</span>}
                                            <span>{entry.label}</span>
                                            {isLateFee && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                                    Late Fee
                                                </span>
                                            )}
                                            {isAdjustment && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                                                    Adjustment
                                                </span>
                                            )}
                                            {entry.target_bill_heads?.name && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                                    {entry.target_bill_heads.name}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right text-rose-600">{entry.debit ? `₹${entry.debit.toLocaleString()}` : '—'}</td>
                                    <td className="px-6 py-4 text-right text-emerald-600">{entry.credit ? `₹${entry.credit.toLocaleString()}` : '—'}</td>
                                    <td className={cn('px-6 py-4 text-right font-bold', (entry.balance || 0) > 0 ? 'text-rose-600' : 'text-slate-900')}>
                                        {entry.balance != null ? `₹${entry.balance.toLocaleString()}` : '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LedgerView;
